import React, { useState } from "react";
import {
  ConfigProvider,
  Form,
  Input,
  Button,
  Card,
  Typography,
  Spin,
  notification,
  Row,
  Col,
  Upload,
  message,
  Checkbox,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  ShopOutlined,
  HomeOutlined,
  PhoneOutlined,
  UploadOutlined,
  IdcardOutlined,
  CheckOutlined,
  SafetyOutlined,
  CustomerServiceOutlined,
  ThunderboltOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_CONFIG from "../../config/api.js";
import { ELSA_BRAND_ASSETS } from "../../config/brandAssets";
import "./Signup.css";

const { Title, Text } = Typography;

const logoWhite = ELSA_BRAND_ASSETS.logoHorizontalWhite;

const normFile = (e) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};

const activityDomainOptions = [
  {
    value: "commercial",
    label: "Gestion Commerciale (achat, vente, production)",
  },
  {
    value: "hotellerie",
    label: "Hotellerie",
  },
  {
    value: "logistique",
    label: "Logistique (transit et freight forwarding)",
  },
  {
    value: "rh",
    label: "Ressources Humaines",
  },
];

const activityDomainGuidance = {
  commercial: {
    title: "Gestion Commerciale: pour piloter votre coeur d'activite commerciale",
    description:
      "Ideal si votre activite principale est l'achat, la vente, la facturation et la gestion des stocks.",
    highlights: [
      "Achats, ventes, retours, paiements entrants/sortants et suivi clients/fournisseurs.",
      "Gestion de stock multi-établissements et point de vente (POS) pour les operations quotidiennes.",
      "Production et transformation: utile pour restaurants, boulangeries, ateliers et petites industries.",
      "Tresorerie, depenses, rapports de performance et pilotage de rentabilite.",
      "E-commerce connecte pour synchroniser produits, stocks et commandes en ligne.",
    ],
    tip:
      "Choisissez ce domaine si vous gerez surtout des flux commerciaux de produits (revente ou production).",
  },
  hotellerie: {
    title: "Hotellerie: pour les operations d'etablissements hoteliers",
    description:
      "Ideal pour hotels, residences et maisons d'hotes qui gerent chambres, reservations et services.",
    highlights: [
      "Gestion des chambres, types de chambres, et disponibilites.",
      "Reservations, clients, folios et encaissements.",
      "Operations hotelieres (housekeeping, maintenance, services).",
      "Depenses et configuration complete de la structure hoteliere.",
    ],
    tip:
      "Choisissez ce domaine si votre activite principale tourne autour de l'hebergement et des sejours.",
  },
  logistique: {
    title: "Logistique: pour transit, freight forwarding et dossiers d'expedition",
    description:
      "Ideal pour les transitaires/transporteurs qui suivent des expeditions et leur rentabilite dossier par dossier.",
    highlights: [
      "Gestion de dossiers d'expedition (maritime, aerien, routier) et suivi operationnel.",
      "Job costing detaille (couts, debours, revenus, marges) par dossier ou unite.",
      "Facturation logistique clients/fournisseurs et suivi des paiements associes.",
      "Suivi conteneurs/colis, tresorerie logistique et rapports dedies.",
    ],
    tip:
      "Choisissez ce domaine si votre activite principale est le transit ou le transport international.",
  },
  rh: {
    title: "Ressources Humaines: pour la gestion du personnel et de la paie",
    description:
      "Ideal pour structurer la gestion RH, le suivi des employes et le traitement de la paie.",
    highlights: [
      "Gestion des employes, contrats, postes et departements.",
      "Traitement de paie, bulletins, elements variables et historiques.",
      "Conformite sociale/fiscale et rapports RH.",
      "Pilotage RH avec tableaux de bord et permissions dediees.",
    ],
    tip:
      "Choisissez ce domaine si votre besoin prioritaire est la gestion du capital humain et de la paie.",
  },
};

const brandPoints = [
  { icon: <CheckOutlined />, text: "14 jours gratuits, sans engagement" },
  { icon: <SafetyOutlined />, text: "Vos donnees securisees et sauvegardees" },
  { icon: <CustomerServiceOutlined />, text: "Support local, en francais" },
  { icon: <ThunderboltOutlined />, text: "Pret a encaisser le jour meme" },
];

const Signup = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const selectedDomain = Form.useWatch("activity_domain", form);
  const signupParams = new URLSearchParams(window.location.search);
  const requestedPlan = /^\d+$/.test(signupParams.get("plan") || "")
    ? signupParams.get("plan")
    : null;
  const requestedBilling = signupParams.get("billing") === "annual"
    ? "annual"
    : "monthly";
  const paymentNextPath = requestedPlan
    ? `/payment?plan=${requestedPlan}&billing=${requestedBilling}`
    : null;

  const handleActivityDomainToggle = (domain, isChecked) => {
    const nextDomain = isChecked ? domain : undefined;
    form.setFieldsValue({ activity_domain: nextDomain });
    form.validateFields(["activity_domain"]).catch(() => {});
  };

  const onFinish = async (values) => {
    setLoading(true);

    const formData = new FormData();

    // Informations sur l'entreprise
    formData.append("company_name", values.company_name);
    formData.append("company_phone", values.company_phone);
    formData.append("company_address", values.company_address || "");
    formData.append("activity_domain", values.activity_domain);

    // Informations sur le magasin
    formData.append("warehouse_name", values.warehouse_name);
    formData.append("warehouse_phone", values.warehouse_phone || "");
    formData.append(
      "warehouse_invoice_prefix",
      values.warehouse_invoice_prefix || ""
    );
    if (values.warehouse_logo && values.warehouse_logo.length > 0) {
      formData.append("warehouse_logo", values.warehouse_logo[0].originFileObj);
    }

    // Informations sur l'utilisateur
    formData.append("user_name", values.user_name);
    formData.append("email", values.email);
    formData.append("password", values.password);
    formData.append("user_phone", values.user_phone);
    if (values.profile_image && values.profile_image.length > 0) {
      formData.append("profile_image", values.profile_image[0].originFileObj);
    }

    try {
      await axios.post(API_CONFIG.apiUrl("/register"), formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      notification.success({
        message: "Inscription réussie !",
        description:
          paymentNextPath
            ? "Votre compte est créé. Connectez-vous pour poursuivre le paiement sécurisé de votre abonnement."
            : "Votre compte a été créé avec succès. Vous allez être redirigé vers la page de connexion.",
        duration: 4,
      });

      // Redirection vers la page de connexion au lieu de connecter automatiquement
      setTimeout(() => {
        navigate(
          paymentNextPath
            ? `/login?next=${encodeURIComponent(paymentNextPath)}`
            : "/login"
        );
      }, 2000); // Délai de 2 secondes pour laisser le temps de lire le message
    } catch (error) {
      console.error("Erreur lors de l'inscription:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Une erreur s'est produite lors de l'inscription.";
      notification.error({
        message: "Échec de l'inscription",
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("Vous ne pouvez importer que des fichiers image !");
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("L'image doit être plus petite que 5MB!");
    }
    return isImage && isLt5M;
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#173f5f",
          colorLink: "#173f5f",
          borderRadius: 12,
          controlHeight: 44,
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        },
      }}
    >
      <div className="signup-shell">
        <div className="signup-card">
          {/* ===== PANNEAU DE MARQUE ===== */}
          <aside className="signup-aside">
            <div className="signup-brand">
              <img src={logoWhite} alt="ELSA GESTION" />
            </div>
            <span className="signup-aside__eyebrow">Création de compte</span>
            <h1 className="signup-aside__title">
              Lancez votre activité avec ELSA GESTION
            </h1>
            <p className="signup-aside__lead">
              Quelques informations et votre espace est prêt. Essai gratuit de 14
              jours, complet et sans engagement.
            </p>
            <div className="signup-points">
              {brandPoints.map((p) => (
                <div className="signup-point" key={p.text}>
                  <span className="signup-point__ic">{p.icon}</span>
                  <span className="signup-point__tx">{p.text}</span>
                </div>
              ))}
            </div>
            <div className="signup-steps">
              <span className="signup-steps__lbl">4 étapes rapides</span>
              <div className="signup-steps__row">
                <span className="signup-step"><span className="n">1</span> Entreprise</span>
                <span className="signup-step"><span className="n">2</span> Activité</span>
                <span className="signup-step"><span className="n">3</span> Établissement</span>
                <span className="signup-step"><span className="n">4</span> Compte</span>
              </div>
            </div>
          </aside>

          {/* ===== FORMULAIRE ===== */}
          <main className="signup-main">
            <div className="signup-head">
              <Title level={2} className="signup-head__title">
                Créez votre espace ELSA GESTION
              </Title>
              <Text type="secondary" className="signup-head__sub">
                Rejoignez-nous et lancez votre essai gratuit de 14 jours. Complet,
                sans engagement.
              </Text>
            </div>

            <Spin
              spinning={loading}
              tip="Création de votre compte en cours..."
              size="large"
            >
              <Form
                form={form}
                name="signup"
                onFinish={onFinish}
                layout="vertical"
                requiredMark="optional"
                className="signup-form"
              >
                {/* SECTION ENTREPRISE */}
                <div className="signup-sechead">
                  <span className="signup-secnum">1</span>
                  <div>
                    <div className="signup-sectitle">Votre entreprise</div>
                    <div className="signup-secsub">Les informations de votre société</div>
                  </div>
                </div>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="company_name"
                      label="Nom de l'entreprise"
                      rules={[
                        {
                          required: true,
                          message: "Le nom de l'entreprise est obligatoire.",
                        },
                      ]}
                    >
                      <Input
                        prefix={<HomeOutlined />}
                        placeholder="Ex: Ma Super Entreprise"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="company_phone"
                      label="Téléphone de l'entreprise"
                      rules={[
                        {
                          required: true,
                          message: "Le téléphone est obligatoire.",
                        },
                      ]}
                    >
                      <Input
                        prefix={<PhoneOutlined />}
                        placeholder="Numéro de contact principal"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="company_address" label="Adresse de l'entreprise">
                  <Input.TextArea
                    rows={2}
                    placeholder="Siège social ou adresse principale"
                    size="large"
                  />
                </Form.Item>

                {/* SECTION DOMAINE D'ACTIVITE */}
                <div className="signup-sechead">
                  <span className="signup-secnum">2</span>
                  <div>
                    <div className="signup-sectitle">Votre domaine d'activité</div>
                    <div className="signup-secsub">Choisissez votre métier principal</div>
                  </div>
                </div>
                <Form.Item
                  label="Domaine d'activite principal"
                  required
                  validateStatus={
                    form.getFieldError("activity_domain").length > 0 ? "error" : ""
                  }
                  help={form.getFieldError("activity_domain")[0]}
                >
                  <>
                    <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
                      {activityDomainOptions.map((option) => {
                        const guidance = activityDomainGuidance[option.value];
                        const isChecked = selectedDomain === option.value;

                        return (
                          <Col xs={24} md={12} key={option.value}>
                            <Card
                              size="small"
                              hoverable
                              className={
                                "signup-domain" +
                                (isChecked ? " is-selected" : "")
                              }
                              onClick={() =>
                                handleActivityDomainToggle(
                                  option.value,
                                  selectedDomain !== option.value
                                )
                              }
                            >
                              <div className="signup-domain__top">
                                <Text strong>{option.label}</Text>
                                <Checkbox
                                  checked={isChecked}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    handleActivityDomainToggle(
                                      option.value,
                                      e.target.checked
                                    )
                                  }
                                />
                              </div>

                              <Text type="secondary">{guidance.description}</Text>
                              <ul className="signup-domain__list">
                                {guidance.highlights.map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                              <Text className="signup-domain__tip">{guidance.tip}</Text>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                    <Text type="secondary">
                      Cochez une seule option correspondant a votre domaine
                      principal.
                    </Text>
                  </>
                </Form.Item>
                <Form.Item
                  name="activity_domain"
                  rules={[
                    {
                      required: true,
                      message: "Veuillez choisir votre domaine d'activite.",
                    },
                  ]}
                  hidden
                >
                  <Input />
                </Form.Item>

                {/* SECTION MAGASIN */}
                <div className="signup-sechead">
                  <span className="signup-secnum">3</span>
                  <div>
                    <div className="signup-sectitle">Votre établissement principal</div>
                    <div className="signup-secsub">Le point de vente rattaché à votre compte</div>
                  </div>
                </div>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="warehouse_name"
                      label="Nom de l'établissement"
                      rules={[
                        {
                          required: true,
                          message: "Le nom de l'établissement est obligatoire.",
                        },
                      ]}
                    >
                      <Input
                        prefix={<ShopOutlined />}
                        placeholder="Ex: Boutique Centrale"
                        size="large"
                      />
                    </Form.Item>
                    <Form.Item
                      name="warehouse_phone"
                      label="Téléphone de l'établissement (si différent)"
                    >
                      <Input
                        prefix={<PhoneOutlined />}
                        placeholder="Laissez vide si identique à l'entreprise"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="warehouse_invoice_prefix"
                      label="Préfixe de facturation"
                      tooltip="Sera ajouté avant chaque numéro de facture. Ex: 'BC-'."
                    >
                      <Input
                        prefix={<IdcardOutlined />}
                        placeholder="Ex: FACT-"
                        size="large"
                      />
                    </Form.Item>
                    <Form.Item
                      name="warehouse_logo"
                      label="Logo de l'établissement (pour les factures)"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        name="warehouse_logo"
                        listType="picture"
                        maxCount={1}
                        beforeUpload={beforeUpload}
                      >
                        <Button icon={<UploadOutlined />}>Importer un logo</Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                </Row>

                {/* SECTION UTILISATEUR */}
                <div className="signup-sechead">
                  <span className="signup-secnum">4</span>
                  <div>
                    <div className="signup-sectitle">Votre compte administrateur</div>
                    <div className="signup-secsub">Vos identifiants de connexion</div>
                  </div>
                </div>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="user_name"
                      label="Votre nom complet"
                      rules={[
                        { required: true, message: "Votre nom est obligatoire." },
                      ]}
                    >
                      <Input
                        prefix={<UserOutlined />}
                        placeholder="Ex: Aminata Ouédraogo"
                        size="large"
                      />
                    </Form.Item>
                    <Form.Item
                      name="email"
                      label="Votre email de connexion"
                      rules={[
                        {
                          required: true,
                          message: "Veuillez entrer votre email !",
                        },
                        { type: "email", message: "L'email est invalide !" },
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined />}
                        placeholder="vous@exemple.com"
                        size="large"
                      />
                    </Form.Item>
                    <Form.Item
                      name="user_phone"
                      label="Votre numéro de téléphone"
                      rules={[
                        {
                          required: true,
                          message: "Votre téléphone est obligatoire.",
                        },
                      ]}
                    >
                      <Input
                        prefix={<PhoneOutlined />}
                        placeholder="Pour la récupération de compte"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="password"
                      label="Créez un mot de passe"
                      rules={[
                        {
                          required: true,
                          message: "Le mot de passe est obligatoire.",
                        },
                        { min: 6, message: "6 caractères minimum." },
                      ]}
                      hasFeedback
                    >
                      <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="Mot de passe sécurisé"
                        size="large"
                      />
                    </Form.Item>
                    <Form.Item
                      name="confirm"
                      label="Confirmez le mot de passe"
                      dependencies={["password"]}
                      hasFeedback
                      rules={[
                        {
                          required: true,
                          message: "Veuillez confirmer votre mot de passe !",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue("password") === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error("Les mots de passe ne correspondent pas !")
                            );
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="Confirmez le mot de passe"
                        size="large"
                      />
                    </Form.Item>
                    <Form.Item
                      name="profile_image"
                      label="Votre photo de profil (facultatif)"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        name="profile_image"
                        listType="picture"
                        maxCount={1}
                        beforeUpload={beforeUpload}
                      >
                        <Button icon={<UploadOutlined />}>
                          Importer une photo
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item style={{ marginTop: "24px", marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="signup-submit"
                    icon={<ArrowRightOutlined />}
                    loading={loading}
                  >
                    Créer mon compte et démarrer l'essai
                  </Button>
                </Form.Item>

                <p className="signup-terms">
                  En créant votre compte, vous démarrez un essai gratuit de 14
                  jours, sans engagement.
                </p>

                <div className="signup-login">
                  Vous avez déjà un compte ? <a href="/login">Connectez-vous ici</a>.
                </div>
                <p className="signup-contact">
                  <a href="https://www.elsagestion.com">www.elsagestion.com</a>
                  <br />
                  <a href="tel:+22664989099">+226 64 98 90 99</a>
                </p>
              </Form>
            </Spin>
          </main>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Signup;
